import React from 'react';
import { Box, HStack, Icon, Menu, Text, VStack } from '@chakra-ui/react';
import { House } from '@/client';
import { ClassifiedHouse } from '@/utils/properties/propertyGroupingUtils';
import { getPropertyMeta } from '@/components/properties/cards/PropertiesCardGrid/propertyCardUtils.ts';
import { splitAddress } from '@/utils/properties/addressUtils.ts';
import { useAuth } from '@/context/AuthContext.tsx';
import PropertySelectorLockedSubscribeCard from './PropertySelectorLockedSubscribeCard.tsx';
import PropertySelectorRowStatus from './PropertySelectorRowStatus.tsx';
import PropertySelectorSectionHeader from './PropertySelectorSectionHeader.tsx';
import {
	PROPERTY_SELECTOR_DESKTOP_ROW_INSET,
	getPropertySelectorRowIcon,
	PROPERTY_SELECTOR_LOCKED_ROW_OPACITY,
	PropertySelectorGroupKey,
} from './propertySelectorSections.ts';
import PropertyCopyTag from '@/components/properties/PropertyCopyTag';
import PropertyNotificationMarker from '@/components/properties/PropertyNotificationMarker';

interface PropertySelectorGroupProps {
	groupKey: string;
	title: string;
	houses: ClassifiedHouse[];
	lockedCount: number;
	selectedHouseId: number;
	onHouseSelect: (house: House) => void;
	isHouseLocked: (houseId: number) => boolean;
	onSubscribe?: () => void;
	isSubscribeLoading?: boolean;
	transferringPropertyIds?: Set<number>;
	unreadNotificationPropertyIds?: Set<number>;
}

const PropertySelectorGroup: React.FC<PropertySelectorGroupProps> = (props) => {
	const {
		groupKey,
		title,
		houses,
		lockedCount,
		selectedHouseId,
		onHouseSelect,
		isHouseLocked,
		onSubscribe,
		isSubscribeLoading,
		transferringPropertyIds,
		unreadNotificationPropertyIds,
	} = props;
	const { user } = useAuth();
	const showSubscribeCard = groupKey === PropertySelectorGroupKey.Shared && lockedCount > 0 && !!onSubscribe;

	return (
		<Menu.ItemGroup _notFirst={{ borderTopWidth: '1px', borderColor: 'border.default', mt: 2, pt: 2 }}>
			<Menu.ItemGroupLabel px={PROPERTY_SELECTOR_DESKTOP_ROW_INSET} pt={2} pb={2}>
				<PropertySelectorSectionHeader title={title} count={houses.length} />
			</Menu.ItemGroupLabel>

			{houses.map((house) => {
				const { street, area } = splitAddress(house.address);
				const isTransferring = transferringPropertyIds?.has(house.id) ?? false;
				const meta = getPropertyMeta(house, user?.id, isTransferring);
				const rowIcon = getPropertySelectorRowIcon(house, user?.id);
				const isSelected = selectedHouseId === house.id;
				const isLocked = isHouseLocked(house.id);
				const rowBg = isSelected ? 'sidebar.active' : 'transparent';
				const hasUnreadNotification = unreadNotificationPropertyIds?.has(house.id) ?? false;

				return (
					<Menu.Item
						cursor={isLocked ? 'default' : 'pointer'}
						key={house.id}
						value={house.id.toString()}
						disabled={isLocked}
						onClick={isLocked ? undefined : () => onHouseSelect(house)}
						w="full"
						mb={0.5}
						px={PROPERTY_SELECTOR_DESKTOP_ROW_INSET}
						py={2}
						borderRadius="2xl"
						bg={isLocked ? 'transparent' : rowBg}
						opacity={isLocked ? PROPERTY_SELECTOR_LOCKED_ROW_OPACITY : 1}
						transition="background-color 120ms ease"
						_hover={isLocked ? undefined : { bg: 'surface.subtle' }}
						_highlighted={isLocked ? undefined : { bg: 'surface.subtle' }}
					>
						<HStack gap={3} w="full" minW={0}>
							<Icon
								as={rowIcon}
								boxSize={4}
								color={isSelected ? 'fg' : 'fg.muted'}
								opacity={isSelected ? 1 : 0.7}
								flexShrink={0}
							/>
							<VStack align="start" gap={0} minW={0} flex={1}>
								<HStack gap={1} minW={0} maxW="full">
									<Text fontSize="md" fontWeight={isSelected ? 'medium' : 'normal'} truncate minW={0}>
										{street}
									</Text>
									<PropertyCopyTag isCopy={house.isCopy} />
									<PropertyNotificationMarker hasUnreadNotification={hasUnreadNotification} />
								</HStack>
								{area && (
									<Text fontSize="xs" color="fg.muted" truncate maxW="full">
										{area}
									</Text>
								)}
							</VStack>
							<PropertySelectorRowStatus
								isLocked={isLocked}
								badge={meta.badge}
								colorPalette={meta.colorPalette}
							/>
						</HStack>
					</Menu.Item>
				);
			})}

			{showSubscribeCard && (
				<Box mx={2}>
					<PropertySelectorLockedSubscribeCard
						lockedCount={lockedCount}
						onSubscribe={onSubscribe}
						isLoading={isSubscribeLoading}
					/>
				</Box>
			)}
		</Menu.ItemGroup>
	);
};

export default PropertySelectorGroup;
